import { Controller } from "@hotwired/stimulus"
import { connectPublicationViewBatch } from "../community/publication_view_batch"

export default class extends Controller {
  static values = { publicationId: Number, endpoint: String, viewerId: Number }
  static targets = ["excluded"]

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return
    this.batch = connectPublicationViewBatch(this.endpointValue, this.viewerIdValue)
    this.active = true
    this.qualified = false
    this.intersecting = !window.IntersectionObserver
    this.visibleSince = null
    this.checkVisibility = this.checkVisibility.bind(this)
    this.visibilityChanged = () => {
      this.visibleSince = null
      cancelAnimationFrame(this.frame)
      this.scheduleFrame()
    }
    this.beforeCache = () => this.disconnect()
    document.addEventListener("visibilitychange", this.visibilityChanged)
    document.addEventListener("turbo:before-cache", this.beforeCache)
    if (window.IntersectionObserver) {
      this.observer = new IntersectionObserver(entries => {
        this.intersecting = entries[entries.length - 1].isIntersecting
        if (!this.intersecting) this.visibleSince = null
        cancelAnimationFrame(this.frame)
        this.scheduleFrame()
      })
      this.observer.observe(this.element)
    }
    this.scheduleFrame()
  }

  disconnect() {
    if (!this.active) return
    this.active = false
    cancelAnimationFrame(this.frame)
    this.observer?.disconnect()
    this.visibleSince = null
    document.removeEventListener("visibilitychange", this.visibilityChanged)
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.batch.disconnect()
  }

  checkVisibility(now) {
    if (!this.active || !this.intersecting || this.qualified || document.hidden) return
    if (this.sufficientlyVisible()) {
      if (this.visibleSince === null) this.visibleSince = now
      if (now - this.visibleSince >= 2000) {
        this.batch.add(this.publicationIdValue)
        this.qualified = true
        return
      }
    } else {
      this.visibleSince = null
    }
    // While on screen, check layout shifts even when no scroll or resize event fires.
    this.scheduleFrame()
  }

  scheduleFrame() {
    if (this.active && this.intersecting && !this.qualified && !document.hidden) {
      this.frame = requestAnimationFrame(this.checkVisibility)
    }
  }

  sufficientlyVisible() {
    if (!this.element.isConnected || document.documentElement.hasAttribute("data-turbo-preview")) return false
    const rect = this.element.getBoundingClientRect()
    // The optional trailing attachment discussion is not publication content.
    const bottom = Math.min(rect.bottom, ...this.excludedTargets.map(target => target.getBoundingClientRect().top))
    const contentHeight = Math.min(rect.height, bottom - rect.top)
    if (contentHeight <= 0 || rect.width <= 0) return false
    const height = Math.max(0, Math.min(bottom, window.innerHeight) - Math.max(rect.top, 0))
    const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0))
    return height >= Math.min(contentHeight, window.innerHeight) / 2 && width >= rect.width / 2
  }
}
